/**
 * Normalize API audit report data to match the frontend AuditData shape.
 * The API returns nested structures from tool results; the UI expects
 * flat arrays matching the mock data format.
 */

import { realValue } from './realValue';

type AnyData = Record<string, unknown>;

function safeArray<T>(val: unknown, key?: string): T[] {
  if (Array.isArray(val)) return val as T[];
  if (val && typeof val === 'object' && key) {
    const nested = (val as AnyData)[key];
    if (Array.isArray(nested)) return nested as T[];
  }
  return [];
}

export function normalizeReportData(api: AnyData): AnyData {
  const out: AnyData = { ...api };

  // healthScore: extract overall score for the gauge
  // API: { health_score: { overall, growth, ... }, dimensions: {...}, summary: "..." }
  // UI expects: { overall: number } at minimum for the gauge
  if (api.healthScore && typeof api.healthScore === 'object') {
    const hs = api.healthScore as AnyData;
    if (hs.health_score && typeof hs.health_score === 'object') {
      out.healthScore = hs.health_score;
    }
  }

  // swot: API returns { swot: { strengths: [...], ... }, counts, summary }
  // UI expects: { strengths: [...], weaknesses: [...], opportunities: [...], threats: [...] }
  if (api.swot && typeof api.swot === 'object') {
    const s = api.swot as AnyData;
    if (s.swot && typeof s.swot === 'object') {
      out.swot = s.swot;
    }
  }

  // businessAreas: API returns { grades: [...], summary }
  // UI expects: array of { area, grade, score, detail, color }
  if (api.businessAreas && typeof api.businessAreas === 'object' && !Array.isArray(api.businessAreas)) {
    const ba = api.businessAreas as AnyData;
    const grades = safeArray<AnyData>(ba, 'grades');
    if (grades.length > 0) {
      const gradeColors: Record<string, string> = {
        'A': '#22c55e', 'B': '#3b82f6', 'C': '#f59e0b', 'D': '#f97316', 'F': '#ef4444',
      };
      out.businessAreas = grades.map((g) => ({
        ...g,
        color: gradeColors[String(g.grade)?.[0]] || '#9ca3af',
      }));
    }
  }

  // peers: API returns { comparison: [...], company_name, your_position, summary }
  // UI expects: array of { name, revenue, growth, subscribers, ltv, churn, cpa, position }
  if (api.peers && typeof api.peers === 'object' && !Array.isArray(api.peers)) {
    const p = api.peers as AnyData;
    const comparison = safeArray<AnyData>(p, 'comparison');
    if (comparison.length > 0) {
      out.peers = comparison.map((c) => ({
        name: c.company || '',
        revenue: c.revenue || '',
        growth: c.growth || '',
        subscribers: c.subscribers || '',
        ltv: c.ltv || '',
        churn: c.churn || '',
        cpa: c.cpa || '',
        position: c.is_self ? 'you' : (c.indicator || ''),
      }));
    }
  }

  // macroRisks: API returns { categories: { economic_climate: [...], ... }, summary, ... }
  // UI expects: { economy: [...], geopolitical: [...], industry: [...], technology: [...] }
  if (api.macroRisks && typeof api.macroRisks === 'object') {
    const mr = api.macroRisks as AnyData;
    const cats = mr.categories as AnyData | undefined;
    if (cats && typeof cats === 'object') {
      const keyMap: Record<string, string> = {
        economic_climate: 'economy',
        geopolitical_factors: 'geopolitical',
        industry_dynamics: 'industry',
        technology_shifts: 'technology',
      };
      const normalized: AnyData = {};
      for (const [apiKey, uiKey] of Object.entries(keyMap)) {
        const items = safeArray<AnyData>(cats, apiKey);
        normalized[uiKey] = items.map((item) => ({
          text: item.text || '',
          impact: String(item.impact || '').toLowerCase().replace(/\s+/g, '-'),
        }));
      }
      out.macroRisks = normalized;
    }
  }

  // channelBenchmarks: API returns { metrics: [...], summary, calculation }
  // UI expects: array of { channel, cpa, trialRate, ltvMultiple, verdict, verdictColor }
  if (api.channelBenchmarks && typeof api.channelBenchmarks === 'object' && !Array.isArray(api.channelBenchmarks)) {
    const cb = api.channelBenchmarks as AnyData;
    const metrics = safeArray<AnyData>(cb, 'metrics');
    // Only surface real measured data — benchmark/estimate placeholders fall
    // back to a dash, never a fabricated number. (see utils/realValue)
    out.channelBenchmarks = metrics.map((m) => {
      const ltv = realValue(m.ltv_cac_ratio);
      const ltvNum = ltv != null ? Number(ltv) : NaN;
      const hasLtv = Number.isFinite(ltvNum);
      return {
        channel: m.channel || 'Unknown',
        cpa: realValue(m.cac_formatted) ?? realValue(m.cpa) ?? '—',
        trialRate: realValue(m.trial_rate) ?? '—',
        ltvMultiple: hasLtv ? `${ltvNum}x` : '—',
        verdict: !hasLtv
          ? (realValue(m.verdict) ?? 'Track')
          : ltvNum > 3 ? 'Scale' : ltvNum > 1 ? 'Optimize' : 'Pause',
        verdictColor: !hasLtv
          ? '#3b82f6'
          : ltvNum > 3 ? '#22c55e' : ltvNum > 1 ? '#f59e0b' : '#ef4444',
      };
    });
  }

  // marketSizing: API returns { tam: { value, label, ... }, sam: {...}, som: {...} }
  // UI expects: { tam: { value, label, ... }, sam: {...}, som: {...} } — similar but ensure value field
  if (api.marketSizing && typeof api.marketSizing === 'object') {
    const ms = api.marketSizing as AnyData;
    // Ensure each tier has both value and label
    for (const tier of ['tam', 'sam', 'som']) {
      const t = ms[tier] as AnyData | undefined;
      if (t && typeof t === 'object') {
        if (!t.label) t.label = t.formatted || '';
      }
    }
    out.marketSizing = ms;
  }

  // keyFindings: API may return as keyfindings, keyFindings, or key_findings
  if (!out.keyFindings) {
    out.keyFindings = api.keyfindings || api.key_findings || [];
  }

  // funnel: normalise dual-funnel snake_case fields → camelCase expected by ConversionFunnel.tsx
  // The refine agent is instructed to do this rename, but we handle it here as a safety net
  // in case it passes snake_case names (trial_funnel / direct_funnel / funnel_combined).
  if (api.funnel && typeof api.funnel === 'object') {
    const f = api.funnel as AnyData;

    // funnelType: accept both camelCase and snake_case
    if (!f.funnelType && f.funnel_type) {
      (out.funnel as AnyData).funnelType = f.funnel_type;
    }

    // trialFunnel: accept trial_funnel[] with volume/conv_pct → value/convPct
    if (!f.trialFunnel && Array.isArray(f.trial_funnel)) {
      (out.funnel as AnyData).trialFunnel = (f.trial_funnel as AnyData[]).map((s) => ({
        stage: s.stage,
        value: s.value ?? s.volume,
        convPct: s.convPct ?? s.conv_pct,
      }));
    }

    // directFunnel: accept direct_funnel[] with volume/conv_pct → value/convPct
    if (!f.directFunnel && Array.isArray(f.direct_funnel)) {
      (out.funnel as AnyData).directFunnel = (f.direct_funnel as AnyData[]).map((s) => ({
        stage: s.stage,
        value: s.value ?? s.volume,
        convPct: s.convPct ?? s.conv_pct,
      }));
    }

    // combined: accept funnel_combined{} with snake_case keys → camelCase
    if (!f.combined && f.funnel_combined && typeof f.funnel_combined === 'object') {
      const fc = f.funnel_combined as AnyData;
      (out.funnel as AnyData).combined = {
        totalPaid:          fc.totalPaid          ?? fc.total_paid,
        totalPaidPct:       fc.totalPaidPct       ?? fc.total_paid_pct,
        trialConverts:      fc.trialConverts      ?? fc.trial_converts,
        // direct_subs is the new preferred field (Direct Subscriptions + Intro Offers);
        // fall back to direct_purchases for reports saved before this change.
        directPurchases:    fc.directPurchases    ?? fc.direct_subs    ?? fc.direct_purchases,
        productChanges:     fc.productChanges     ?? fc.product_changes,
        resubscriptions:    fc.resubscriptions,
        trialSharePct:      fc.trialSharePct      ?? fc.trial_share_pct,
        directSharePct:     fc.directSharePct     ?? fc.direct_share_pct,
        productChangesPct:  fc.productChangesPct  ?? fc.product_changes_pct,
        resubscriptionsPct: fc.resubscriptionsPct ?? fc.resubscriptions_pct,
      };
    }
  }

  // scenarios: API returns { scenarios: [...], inputs, insight }
  // Keep as-is, the MarketSizing component handles this

  return out;
}
